import { FeedbacksTable } from '@/components/feedbacks/feedbacks-table';
import { InfoPage } from '@/components/info-page';
import { TableFilterDropdown } from '@/components/table/table-filter-dropdown';
import { TableFilterInputField } from '@/components/table/table-filter-field';
import { TableFilterPlainDatePicker } from '@/components/table/table-filter-plain-date-picker';
import { TableFilterResetButton } from '@/components/table/table-filter-reset-button';
import { TableMoreFiltersMenu } from '@/components/table/table-more-filters-menu';
import { DropdownMenuItem } from '@/components/ui/dropdown-menu';
import { buildNameLikeSelectOptions } from '@/components/ui/multi-select-utils';
import { useAllSchools, useCoachById } from '@/lib/api-call-hooks';
import { EmptyArray, getSessionFeedbacks } from '@/lib/api-calls';
import { useRowsBulkSelection } from '@/lib/bulk-selection';
import { usePaginatedFilters } from '@/lib/filtering';
import { paginationRequestSchema } from '@/lib/pagination';
import { validateRouteRole } from '@/lib/routing';
import { BulkUpdateLateFeedback } from '@/routes/_layout/sessions/-components/bulk-update-late-feedback';
import { BulkUpdateStudentAttendance } from '@/routes/_layout/sessions/-components/bulk-update-student-attended';
import { sessionsTabs } from '@/routes/_layout/sessions/-components/tabs';
import { distinct, FrequentTimeZones, isAirAdmin, isNotNullish, PrismaEnums } from '@airreading/common';
import {
  GetSessionFeedbacksModifier,
  lessonKindSchema,
  LessonKindToLabel,
  SessionFeedbackWithAssessment,
  studentYearOption,
  toSessionFeedbackCompoundId,
} from '@airreading/common/data';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useMemo } from 'react';
import { Temporal } from 'temporal-polyfill';
import { z } from 'zod';

export const Route = createFileRoute('/_layout/sessions/session-feedbacks/')({
  beforeLoad: validateRouteRole(isAirAdmin),
  component: SessionFeedbacksIndex,
  validateSearch: paginationRequestSchema.extend({
    feedbackSubmitted: z.boolean().optional(),
    coachId: z.string().optional(),
    lessonIds: z.string().array().optional(),
    grades: z.array(z.nativeEnum(PrismaEnums.StudentYear)).optional(),
    query: z.string().optional(),
    schoolIds: z.string().array().optional(),
    orgIds: z.string().array().optional(),
    sessionPlainDateInUSCentral: z.string().optional(),
    lessonKinds: z.array(lessonKindSchema).optional(),
  }),
});

function SessionFeedbacksIndex() {
  const {
    pageIndex,
    pageSize,
    onPaginationChange,
    filters: {
      query: [[query, activeQuery], setQuery],
      grades: [[grades, activeGrades], setGrades],
      feedbackSubmitted: [[feedbackSubmitted, activeFeedbackSubmitted], setFeedbackSubmitted],
      coachId: [[coachId, activeCoachId], setSelectedCoachId],
      lessonIds: [[selectedLessonIds, activeLessonIds], setLessonIds],
      schoolIds: [[schoolIds, activeSchoolIds], setSchoolIds],
      orgIds: [[orgIds, activeOrgIds], setOrgIds],
      sessionPlainDateInUSCentral: [
        [sessionPlainDateInUSCentral, activeSessionPlainDateInUSCentral],
        setSessionPlainDateInUSCentral,
      ],
      lessonKinds: [[selectedLessonKinds, activeSelectedLessonKinds], setSelectedLessonKinds],
    },
    resetFilters,
    activeFiltersCount,
  } = usePaginatedFilters(Route.id, [
    'query',
    'grades',
    'feedbackSubmitted',
    'coachId',
    'lessonIds',
    'schoolIds',
    'orgIds',
    'sessionPlainDateInUSCentral',
    'lessonKinds',
  ]);

  const requestQuery = {
    pageIndex,
    pageSize,
    filters: {
      feedbackSubmitted: activeFeedbackSubmitted,
      coachId: activeCoachId,
      lessonId: activeLessonIds?.[0],
      query: activeQuery,
      grades: activeGrades,
      schoolIds: activeSchoolIds,
      orgIds: activeOrgIds,

      // If activeSessionPlainDateInUSCentral is set, set is as both startOnOrAfter and startOnOrBefore
      // API would then treat it as startOnOrAfter the start of the day and startOnOrBefore the end of the day
      // If not set, return all session feedbacks on or before today
      startOnOrAfterPlainDateInUSCentral: activeSessionPlainDateInUSCentral
        ? activeSessionPlainDateInUSCentral
        : undefined,
      startOnOrBeforePlainDateInUSCentral: activeSessionPlainDateInUSCentral
        ? activeSessionPlainDateInUSCentral
        : Temporal.Now.plainDateTimeISO(FrequentTimeZones.UsCentral).toPlainDate().toString(),
      lessonKinds: activeSelectedLessonKinds,
    },
  } satisfies GetSessionFeedbacksModifier;

  const { data: allSchools, isPending: isLoadingSchools } = useAllSchools();
  const allSchoolOptions = allSchools.map(buildNameLikeSelectOptions);
  const allOrgOptions = allSchools
    .map((school) => school.org)
    .filter(distinct((o) => o.id))
    .map(buildNameLikeSelectOptions);

  const {
    data: sessionFeedbacksResp,
    isPending: isLoadingSessionFeedbacks,
    error,
  } = useQuery(getSessionFeedbacks(requestQuery));

  const sessionFeedbacks = useMemo(() => sessionFeedbacksResp?.sessionFeedbacks || EmptyArray, [sessionFeedbacksResp]);
  const queryClient = useQueryClient();

  const { rowSelection, rowDataSelection, onRowSelectionChange, getRowId, resetAllRowSelection } =
    useRowsBulkSelection<SessionFeedbackWithAssessment>({
      getRowId: (row) => toSessionFeedbackCompoundId(row),
      rowIdToData: (id) => sessionFeedbacks.find((feedback) => toSessionFeedbackCompoundId(feedback) === id),
    });

  const fetchCoachById = useCoachById();

  const filterLeftComponents = useMemo(
    () => (
      <>
        <TableFilterInputField
          className="w-[250px] lg:w-[350px] xl:w-[400px] 2xl:w-[450px]"
          placeholder="Search sessions by student, teacher, email or lesson name"
          value={query ?? ''}
          onChange={(event) => {
            setQuery(event.target.value);
          }}
        />
        <TableFilterPlainDatePicker
          onPlainDateSelect={setSessionPlainDateInUSCentral}
          selectedPlainDateString={sessionPlainDateInUSCentral}
          onClear={() => setSessionPlainDateInUSCentral(undefined)}
          tableFilterDatePickerProps={{
            placeholder: 'Filter by session date (CT)',
          }}
        />
        <TableFilterDropdown
          title="Feedback submitted?"
          options={[
            { value: true, label: 'Submitted' },
            { value: false, label: 'Not submitted' },
          ]}
          selectedValues={[feedbackSubmitted].filter(isNotNullish)}
          onChange={(selected) => {
            setFeedbackSubmitted(selected.filter((v) => v !== feedbackSubmitted)[0]);
          }}
        />
        <TableMoreFiltersMenu
          activeFiltersCount={activeFiltersCount}
          filterItems={[
            {
              title: 'Lesson type',
              filterComponent: (
                <TableFilterDropdown
                  title="Lesson type"
                  options={lessonKindSchema.options.map((type) => ({
                    label: LessonKindToLabel[type],
                    value: type,
                  }))}
                  selectedValues={selectedLessonKinds ?? []}
                  onChange={setSelectedLessonKinds}
                />
              ),
            },
            {
              title: 'District',
              filterComponent: (
                <TableFilterDropdown
                  title="District"
                  options={allOrgOptions}
                  selectedValues={orgIds ?? []}
                  onChange={setOrgIds}
                  isLoading={isLoadingSchools}
                />
              ),
            },
            {
              title: 'School',
              filterComponent: (
                <TableFilterDropdown
                  title="School"
                  options={allSchoolOptions}
                  selectedValues={schoolIds ?? []}
                  onChange={setSchoolIds}
                  isLoading={isLoadingSchools}
                />
              ),
            },
            {
              title: 'Grade',
              filterComponent: (
                <TableFilterDropdown
                  title="Grade"
                  options={Object.values(PrismaEnums.StudentYear).map((grade) => ({
                    label: studentYearOption(grade),
                    value: grade,
                  }))}
                  selectedValues={grades ?? []}
                  onChange={setGrades}
                />
              ),
            },
          ]}
        />
        <TableFilterResetButton show onClick={resetFilters} />
      </>
    ),
    [
      query,
      setSessionPlainDateInUSCentral,
      sessionPlainDateInUSCentral,
      feedbackSubmitted,
      allOrgOptions,
      orgIds,
      setOrgIds,
      isLoadingSchools,
      allSchoolOptions,
      schoolIds,
      setSchoolIds,
      queryClient,
      selectedLessonIds,
      setLessonIds,
      coachId,
      grades,
      setGrades,
      selectedLessonKinds,
      setSelectedLessonKinds,
      resetFilters,
      setQuery,
      setFeedbackSubmitted,
      setSelectedCoachId,
      fetchCoachById,
    ],
  );
  if (error) {
    return (
      <InfoPage headerText="Sessions" tabs={sessionsTabs('Student feedbacks')}>
        <div>Failed to fetch class feedback entries. Please try again or notify Air Reading.</div>
      </InfoPage>
    );
  }
  return (
    <InfoPage headerText="Sessions" tabs={sessionsTabs('Student feedbacks')}>
      <FeedbacksTable
        sessionFeedbacks={sessionFeedbacks}
        isLoading={isLoadingSessionFeedbacks}
        tableHookProps={{
          state: {
            rowSelection,
            pagination: {
              pageIndex,
              pageSize,
            },
          },
          onPaginationChange,
          onRowSelectionChange,
          getRowId,
          rowCount: sessionFeedbacksResp?.total || 0,
        }}
        filters={{
          filterLeftComponents,
        }}
        bulkActionsMenuItems={
          <>
            <BulkUpdateStudentAttendance
              key={'cancel-attendance'}
              selectedFeedbacks={rowDataSelection}
              trigger={
                <DropdownMenuItem onSelect={(e) => e.preventDefault()}>Cancel student attendance</DropdownMenuItem>
              }
              attended={false}
              onSuccess={resetAllRowSelection}
            />
            <BulkUpdateStudentAttendance
              key={'mark-attendance'}
              selectedFeedbacks={rowDataSelection}
              trigger={
                <DropdownMenuItem onSelect={(e) => e.preventDefault()}>Mark student as attended</DropdownMenuItem>
              }
              attended={true}
              onSuccess={resetAllRowSelection}
            />
            <BulkUpdateLateFeedback
              lateFeedbackDismissed={true}
              trigger={
                <DropdownMenuItem onSelect={(e) => e.preventDefault()}>Dismiss late feedback tag</DropdownMenuItem>
              }
              selectedFeedbacks={rowDataSelection}
              onSuccess={resetAllRowSelection}
            />
          </>
        }
      />
    </InfoPage>
  );
}
